import { useRequestQueue } from '@/mixins/requestQueue'
import { windowRequestQueueConfig } from '@/mixins/requestQueueWindowConfig'
import type { CloudSyncInterface } from '@/services/CloudSync/cloudSyncService'
import { Feature, useFeatureService } from '@/services/featureService'
import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import type { StoreDocument, StoreDocumentMeta, UploadItem } from '@/types'
import { captureException } from '@sentry/vue'
import { useDebounceFn } from '@vueuse/core'
import localforageLib from 'localforage'
import { computed, ref } from 'vue'

export type DocumentUpsertRequest = {
	planId: string
	lastUpdated?: string | null
	file: File
	meta: StoreDocumentMeta
}

type DocumentFetchResponseV1 = {
	data: DocumentRecordV1[]
}

export type DocumentRecordV1 = {
	id: string
	lastUpdated: string
	meta: StoreDocumentMeta //We expect meta is returned as the exact format that we upserted
	blob: string //base64 encoded string
}

export interface CloudSyncDocumentInterface
	extends CloudSyncInterface<StoreDocument> {
	fetchFromServer: (planId: string) => Promise<void>
	getDocumentById: (documentId: string) => Promise<StoreDocument | null>
}
export const useCloudSyncDocumentService = (
	logFn: ((message: string) => void) | undefined = undefined
): CloudSyncDocumentInterface => {
	const serviceName = 'document'
	const documentsToUpsert = ref<Set<string>>(new Set<string>())
	const debouncePeriod = Number(
		import.meta.env.VITE_CLOUD_SYNC_DOCUMENT_DEBOUNCE || 3000
	)
	const featureService = useFeatureService()

	const localDocuments = localforageLib.createInstance({
		name: 'documents',
	})

	const cloudSyncDocuments = localforageLib.createInstance({
		name: 'cloudSyncDocuments',
	})

	const debugLog = (message: string): void => {
		if (logFn) {
			logFn('[Document] ' + message)
		}
	}

	const upsertQueueConfig = {
		axiosInstance: purchaseApiAxiosInstance,
		requestBackgroundSync: async (tag: string) => {
			await windowRequestQueueConfig.requestBackgroundSync('cloud-sync-upsert')
		},
		clearCompleted: true,
	}

	const upsertQueue = useRequestQueue(
		'cloudSyncDocumentUpsert',
		upsertQueueConfig
	)

	const checkDocumentsToUpsert = () => {
		documentsToUpsert.value.forEach((documentId) => {
			upsertDocument(documentId).catch(() =>
				console.error('Failed upserting documentId: ' + documentId)
			)
		})
	}

	const flushPendingUpserts = async () => {
		try {
			await Promise.all(
				Array.from(documentsToUpsert.value).map(async (documentId: string) => {
					return upsertDocument(documentId).catch(() =>
						console.error('Failed upserting documentId: ' + documentId)
					)
				})
			)

			debugLog('Successfully flushed document sync queue')
		} catch (e) {
			debugLog('Failed to flush document sync queue')
		}
	}

	const upsertDocuments = useDebounceFn(
		checkDocumentsToUpsert,
		debouncePeriod,
		{
			maxWait: debouncePeriod,
		}
	)

	const upsertDocument = async (documentId: string) => {
		return navigator.locks.request(
			`cloud-sync-document-${documentId}`,
			async () => {
				debugLog(`Starting upsert for documentId: ${documentId}`)
				const document = await localDocuments.getItem<StoreDocument>(documentId)

				if (!document || !validateForUpsert(document)) {
					debugLog(`documentId: ${documentId} not valid for Upsert`)
					documentsToUpsert.value.delete(documentId)
					return
				}

				const requestData = mapUpsertDocumentData(document)

				const upsertRequestConfig = {
					url: `/v1/cloud-sync/document/${document.id}`,
					method: 'POST',
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					data: requestData,
				}

				try {
					debugLog(
						`Queuing Axios request for DocumentId: ${documentId} of the type ${document.meta.type} for ${document.meta.planId}`
					)
					await upsertQueue.addRequest(documentId, upsertRequestConfig)
				} catch (error: any) {
					console.error(
						`Error occurred while trying to upsert document ${documentId}`,
						error
					)

					const hasUnexpectedResponse =
						error.response && error.response.status !== 401
					const hasUnrecoverableError = !error.response

					if (hasUnexpectedResponse || hasUnrecoverableError) {
						captureException(error, {
							extra: {
								message:
									'Unexpected error happened during upserting Document to CloudSync',
							},
						})
					}
				} finally {
					debugLog(`Removing documentId ${documentId} from queue`)
					documentsToUpsert.value.delete(documentId)
				}
			}
		)
	}

	const mapUpsertDocumentData = (
		document: StoreDocument
	): DocumentUpsertRequest => {
		const file = new File([document.blob], `${document.meta.name}`, {
			type: 'application/pdf',
		})
		return {
			planId: document.meta.planId!,
			lastUpdated: document.lastUpdated,
			file: file,
			meta: document.meta,
		}
	}

	const getPlanIdsFromUploadItem = (
		upsertRequest: UploadItem<DocumentUpsertRequest>
	) => {
		return [upsertRequest.requestConfig?.data?.planId!]
	}

	const registerForUpsert = async (documentId: string) => {
		if (!featureService.isEnabled(Feature.CloudSync)) {
			return
		}

		debugLog(`DocumentId: ${documentId} registered for upsert.`)

		documentsToUpsert.value.add(documentId)

		// call throttled upsert
		upsertDocuments().catch()
	}

	const validateForUpsert = (document: any): boolean => {
		return !!document.meta.planId
	}

	const base64ToArrayBuffer = (base64: string) => {
		const binaryString = atob(base64) // Decode the Base64 string to a binary string
		const len = binaryString.length
		const bytes = new Uint8Array(len) // Create an 8-bit unsigned integer array

		for (let i = 0; i < len; i++) {
			bytes[i] = binaryString.charCodeAt(i) // Populate the array with the byte values
		}

		return bytes.buffer // Return the underlying ArrayBuffer
	}

	/**
	 * Fetch Documents by a PlanId and put them in Local Forage
	 */
	const fetchFromServer = async (planId: string) => {
		try {
			const response =
				await purchaseApiAxiosInstance.get<DocumentFetchResponseV1>(
					`v1/cloud-sync/fetch-documents/${planId}`
				)

			for (const item of response.data.data) {
				const fileBuffer: ArrayBuffer = base64ToArrayBuffer(item.blob)
				const document: StoreDocument = {
					id: item.id,
					blob: fileBuffer,
					meta: {
						...item.meta,
						supplemental: String(item.meta.supplemental) === 'true',
					},
					lastUpdated: item.lastUpdated,
				}
				// Store the documents into the cloudSyncDocuments indexedDB table
				await cloudSyncDocuments.setItem(item.id, document)
			}
		} catch (error) {
			console.error(`Failed to fetch CloudSync documents at this time.`, error)
		}
	}

	const getDocumentById = async (
		documentId: string
	): Promise<StoreDocument | null> => {
		return await cloudSyncDocuments.getItem(documentId)
	}

	const retrySync = async () => {
		try {
			await upsertQueue.retryAll()
		} catch (e) {
			console.error(`Some documents were not able to be upserted at this try.`)
		}
	}

	const pendingDebounces = computed((): number => documentsToUpsert.value.size)

	const clearStorage = async () => {
		await cloudSyncDocuments.clear()
	}

	const purge = async () => {
		await clearStorage()
		await upsertQueue.clearPendingRequests()
	}

	const getRelatedPlanIdsForPendingUpserts = async () => {
		const uniquePlanIds = new Set(
			await upsertQueue.getRelatedPlanIdsForPendingUpserts<DocumentUpsertRequest>(
				getPlanIdsFromUploadItem
			)
		)

		return Array.from(uniquePlanIds)
	}

	return {
		debouncePeriod,
		upsertQueue,
		registerForUpsert,
		validateForUpsert,
		fetchFromServer,
		getDocumentById,
		retrySync,
		clearStorage,
		purge,
		pendingDebounces,
		flushPendingUpserts,
		serviceName,
		getRelatedPlanIdsForPendingUpserts,
	}
}
